/**
 * NotificationOptOutsPage — Tasks & Templates · Opt-Outs.
 *
 * Compliance backbone: GLBA annual privacy notice + CAN-SPAM honor-the-opt-out
 * are non-negotiable. The Wave-8 dispatcher consults this catalog before every
 * channel send and skips matching messages with `outcome=skipped-optout`.
 *
 * A row scopes an opt-out by recipient + (optional) event code + (optional)
 * channel. NULL event = all events; NULL channel = all channels. The
 * "all events / all channels" combo is the customer-facing global opt-out
 * ("Unsubscribe me from everything"). "Restoring" a row marks RestoredAt =
 * the dispatcher resumes matching sends; do it only at the recipient's request.
 *
 * Contract (primary sources, 2026-05-18):
 *   - Read:  ConfigApi.notificationOptOuts() → GET /v3/config/notification-opt-outs
 *            envelope { success, data: NotifOptOut[] }
 *   - Write: AdminApi.notificationOptOuts.upsert → POST /v3/admin/notification-opt-outs
 *   - Restore: AdminApi.notificationOptOuts.restore → POST /v3/admin/notification-opt-outs/{id}/restore
 *            (sets RestoredAt; there is NO DELETE route — fixed 2026-06-17)
 *
 * Migrated 2026-06-16 from ROP-teams Admin (operational app writes no config).
 * Auth: ADMIN_AUTH_TOKEN_SENTINEL (MSAL Path B; no bypass-key in prod bundle).
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ADMIN_AUTH_TOKEN_SENTINEL,
  ApiError,
} from '@retail-ops/api-client';
import type { NotifOptOut, NotifOptOutUpsert } from '@retail-ops/shared-types';
import { useApiClient } from '@/contexts/ApiClientContext';
import { Banner, Button, Card, Pill, Spinner } from '@/components/primitives';
import { DataTable, type DataTableColumn } from '@/components/DataTable';
import { TextField, SelectField } from '@/components/FormField';
import { ModalShell } from '@/components/ModalShell';

interface EditDraft {
  isNew: boolean;
  id?: string;
  recipientAddress: string;
  eventCode: string;
  channel: string;
  optOutReason: string;
}

// Channel set is small + enumerable; '' = all channels. Server is free-form
// nvarchar so this is a UX guardrail, not a hard rule.
const CHANNEL_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: '', label: 'All channels' },
  { value: 'email', label: 'email' },
  { value: 'teams-dm', label: 'teams-dm' },
  { value: 'teams-card', label: 'teams-card' },
  { value: 'sms', label: 'sms' },
  { value: 'in-app', label: 'in-app' },
];

const CHANNEL_FILTER_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: '', label: 'All channels' },
  ...CHANNEL_OPTIONS.slice(1),
];

function emptyDraft(): EditDraft {
  return {
    isNew: true,
    recipientAddress: '',
    eventCode: '',
    channel: '',
    optOutReason: '',
  };
}

function rowToDraft(r: NotifOptOut): EditDraft {
  return {
    isNew: false,
    id: r.id,
    recipientAddress: r.recipientAddress,
    eventCode: r.eventCode ?? '',
    channel: r.channel ?? '',
    optOutReason: r.optOutReason ?? '',
  };
}

function draftToUpsertBody(d: EditDraft): NotifOptOutUpsert {
  return {
    ...(d.id ? { id: d.id } : {}),
    recipientAddress: d.recipientAddress.trim(),
    eventCode: d.eventCode.trim() ? d.eventCode.trim() : null,
    channel: d.channel.trim() ? d.channel.trim() : null,
    optOutReason: d.optOutReason.trim() ? d.optOutReason.trim() : null,
  };
}

function formatTs(iso: string | undefined | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

export function NotificationOptOutsPage(): JSX.Element {
  const client = useApiClient();
  const [rows, setRows] = useState<ReadonlyArray<NotifOptOut> | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [channelFilter, setChannelFilter] = useState('');
  const [draft, setDraft] = useState<EditDraft | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [restoreTarget, setRestoreTarget] = useState<NotifOptOut | null>(null);
  const [toast, setToast] = useState<{ tone: 'info' | 'warn'; text: string } | null>(null);
  const [refreshNonce, setRefreshNonce] = useState(0);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setRows(null);
      setLoadError(null);
      try {
        // includeRestored so the editor shows the full audit trail, not just
        // currently-active opt-outs.
        const resp = (await client.retail.config.notificationOptOuts({
          includeRestored: true,
        })) as unknown as {
          success: boolean;
          error?: string;
          data?: ReadonlyArray<NotifOptOut>;
        };
        if (cancelled) return;
        if (resp.success && Array.isArray(resp.data)) {
          setRows(resp.data);
        } else {
          setLoadError(resp.error ?? 'Failed to load opt-outs.');
        }
      } catch (e) {
        if (cancelled) return;
        setLoadError(
          ApiError.isApiError(e) ? e.message : e instanceof Error ? e.message : String(e),
        );
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [client, refreshNonce]);

  const refresh = useCallback(() => setRefreshNonce((n) => n + 1), []);

  const filtered = useMemo(() => {
    if (!rows) return rows;
    const n = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (channelFilter && (r.channel ?? '') !== channelFilter) return false;
      if (n) {
        const hay =
          `${r.recipientAddress} ${r.eventCode ?? ''} ${r.channel ?? ''} ${r.optOutReason ?? ''}`.toLowerCase();
        if (!hay.includes(n)) return false;
      }
      return true;
    });
  }, [rows, channelFilter, search]);

  const columns: ReadonlyArray<DataTableColumn<NotifOptOut>> = useMemo(
    () => [
      {
        header: 'Recipient',
        cell: (r) => <code className="cell-mono--bold">{r.recipientAddress}</code>,
      },
      {
        header: 'Event scope',
        cell: (r) =>
          r.eventCode ? (
            <code className="cell-mono">{r.eventCode}</code>
          ) : (
            <span className="cell-desc"><em>All events</em></span>
          ),
      },
      {
        header: 'Channel',
        cell: (r) =>
          r.channel ? <Pill tone="info">{r.channel}</Pill> : <span className="cell-desc"><em>All</em></span>,
      },
      {
        header: 'Reason',
        cell: (r) => <span className="cell-desc">{r.optOutReason ?? '—'}</span>,
      },
      {
        header: 'Opted-out',
        cell: (r) => <span className="cell-desc">{formatTs(r.optOutAt)}</span>,
      },
      {
        header: 'Status',
        cell: (r) =>
          r.restoredAt ? <Pill tone="muted">restored</Pill> : <Pill tone="warn">active</Pill>,
      },
      {
        header: '',
        cell: (r) => (
          <div className="card-actions-row">
            <Button onClick={() => setDraft(rowToDraft(r))}>Edit</Button>
            {!r.restoredAt && (
              <Button onClick={() => setRestoreTarget(r)}>Restore</Button>
            )}
          </div>
        ),
      },
    ],
    [],
  );

  const handleSave = useCallback(async () => {
    if (!draft) return;
    if (!draft.recipientAddress.trim()) {
      setSaveError('Recipient address (email, UPN, or phone) is required.');
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      const resp = await client.retail.admin.notificationOptOuts.upsert(
        ADMIN_AUTH_TOKEN_SENTINEL,
        draftToUpsertBody(draft),
      );
      if (!resp.success) {
        setSaveError(resp.error ?? 'admin upsert returned success=false');
        return;
      }
      const addr = draft.recipientAddress.trim();
      setDraft(null);
      setToast({ tone: 'info', text: `Opt-out saved for ${addr}.` });
      refresh();
    } catch (e) {
      setSaveError(
        ApiError.isApiError(e) ? e.message : e instanceof Error ? e.message : String(e),
      );
    } finally {
      setSaving(false);
    }
  }, [draft, client, refresh]);

  const handleRestore = useCallback(async () => {
    if (!restoreTarget) return;
    setSaving(true);
    setSaveError(null);
    try {
      const resp = await client.retail.admin.notificationOptOuts.restore(
        ADMIN_AUTH_TOKEN_SENTINEL,
        restoreTarget.id,
      );
      if (!resp.success) {
        setSaveError(resp.error ?? 'restore returned success=false');
        return;
      }
      const addr = restoreTarget.recipientAddress;
      setRestoreTarget(null);
      setToast({ tone: 'warn', text: `Restored sends for ${addr}. Dispatcher will resume matching messages.` });
      refresh();
    } catch (e) {
      setSaveError(
        ApiError.isApiError(e) ? e.message : e instanceof Error ? e.message : String(e),
      );
    } finally {
      setSaving(false);
    }
  }, [restoreTarget, client, refresh]);

  if (loadError) {
    return (
      <Banner tone="err">
        Could not load opt-outs: {loadError} <Button onClick={refresh}>Retry</Button>
      </Banner>
    );
  }
  if (!rows) return <Spinner label="Loading opt-outs…" />;

  return (
    <>
      {toast && (
        <Banner tone={toast.tone}>
          {toast.text} <Button onClick={() => setToast(null)}>Dismiss</Button>
        </Banner>
      )}

      <Card
        title={`Opt-Outs — ${filtered?.length ?? 0} of ${rows.length}`}
        actions={
          <div className="card-actions-row">
            <div className="users-filter">
              <SelectField
                label=""
                value={channelFilter}
                onChange={setChannelFilter}
                options={CHANNEL_FILTER_OPTIONS}
              />
            </div>
            <div className="users-filter">
              <TextField
                label=""
                value={search}
                onChange={setSearch}
                placeholder="Filter by recipient, event, channel, or reason…"
              />
            </div>
            <Button variant="primary" onClick={() => setDraft(emptyDraft())}>
              + Add Opt-Out
            </Button>
          </div>
        }
      >
        <DataTable<NotifOptOut>
          columns={columns}
          rows={filtered ?? []}
          rowKey={(r) => r.id}
          emptyState={<p className="empty">No opt-outs match the current filter.</p>}
        />
      </Card>

      <ModalShell
        open={draft !== null}
        title={draft?.isNew ? 'Add Opt-Out' : `Edit opt-out — ${draft?.recipientAddress ?? ''}`}
        onClose={() => {
          if (saving) return;
          setDraft(null);
          setSaveError(null);
        }}
        width={620}
        actions={
          draft && (
            <>
              <Button onClick={() => setDraft(null)} disabled={saving}>
                Cancel
              </Button>
              <Button variant="primary" onClick={() => void handleSave()} disabled={saving}>
                {saving ? 'Saving…' : draft.isNew ? 'Create' : 'Save changes'}
              </Button>
            </>
          )
        }
      >
        {draft && (
          <div className="form-stack">
            {saveError && <Banner tone="err">{saveError}</Banner>}

            <TextField
              label="Recipient address"
              value={draft.recipientAddress}
              onChange={(v) => setDraft({ ...draft, recipientAddress: v })}
              placeholder="e.g. customer@example.com"
              hint="Email, UPN, or phone — whatever the dispatcher would target."
            />

            <TextField
              label="Event scope"
              value={draft.eventCode}
              onChange={(v) => setDraft({ ...draft, eventCode: v })}
              placeholder="e.g. anniversary-1y"
              hint="Leave blank to opt out of every event. Set a single code to scope the opt-out."
            />

            <SelectField
              label="Channel scope"
              value={draft.channel}
              onChange={(v) => setDraft({ ...draft, channel: v })}
              options={CHANNEL_OPTIONS}
              hint="Restrict the opt-out to a single channel, or leave 'All channels' to suppress everything."
            />

            <TextField
              label="Reason"
              value={draft.optOutReason}
              onChange={(v) => setDraft({ ...draft, optOutReason: v })}
              placeholder="e.g. Phone request from customer 2026-04-25"
              hint="Audit-only. Record how the opt-out was requested (phone, unsubscribe link, branch)."
            />
          </div>
        )}
      </ModalShell>

      <ModalShell
        open={restoreTarget !== null}
        title="Restore sends for this recipient?"
        onClose={() => {
          if (saving) return;
          setRestoreTarget(null);
          setSaveError(null);
        }}
        width={520}
        actions={
          restoreTarget && (
            <>
              <Button onClick={() => setRestoreTarget(null)} disabled={saving}>
                Keep opted-out
              </Button>
              <Button variant="primary" onClick={() => void handleRestore()} disabled={saving}>
                {saving ? 'Working…' : 'Restore sends'}
              </Button>
            </>
          )
        }
      >
        {restoreTarget && (
          <div className="form-stack">
            {saveError && <Banner tone="err">{saveError}</Banner>}
            <p className="modal-prose">
              We'll mark the opt-out for <strong>{restoreTarget.recipientAddress}</strong> as
              restored. The dispatcher will resume matching sends. Only do this when the recipient
              has explicitly asked to be re-subscribed (CAN-SPAM / GLBA compliance).
            </p>
          </div>
        )}
      </ModalShell>
    </>
  );
}
